import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PageHeader, StatCard, Section } from "../../components/shared/Primitives";
import EntryTable from "../../components/shared/EntryTable";
import EntryModal from "../../components/shared/EntryModal";
import CategoryManager from "../../components/shared/CategoryManager";
import { Plus, Search, Settings } from "lucide-react";
import { toast } from "sonner";

export default function OperationsPage({
  kind,
  title,
  eyebrow,
  dot,
  description,
  scope,
  defaultCategories, // [{key, label, color, fields}]
  getFieldsFor, // (categoryName) => fields[]
  columns,
  summaryFields = ["total", "completed", "open", "priority", "cancelled", "followups"],
  decorateTitle,
  extraRowActions, // (row, refresh) => ReactNode | null
  sortRowsBy, // (a, b) => number — optional per-section sort
  extraHeaderActions, // ReactNode rendered between Navigator and Categories button
  injectRowFor, // (categoryName) => row | null  — synthetic row merged into section before sort
  renderRows, // (rows, {onRowClick, refresh}) => ReactNode — overrides EntryTable inside each section
  rowClass, // (row) => className — optional override for the default rowClass logic
  prependSections, // ({scope, entries, refresh}) => ReactNode — extra content BEFORE the categories loop
}) {
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modal, setModal] = useState({ open: false, entry: null, defaults: {} });
  const [q, setQ] = useState("");
  const [catMgrOpen, setCatMgrOpen] = useState(false);
  const seededRef = useRef(false);

  const loadCategories = async () => {
    let { data } = await api.get(`/categories/${kind}`);
    if ((!data || data.length === 0) && defaultCategories?.length && !seededRef.current) {
      seededRef.current = true;
      // seed in parallel; backend is idempotent on (user, kind, name)
      await Promise.all(
        defaultCategories.map((c, i) =>
          api.post(`/categories/${kind}`, {
            name: c.key,
            color: c.color,
            order: i,
            hidden: false,
          })
        )
      );
      const r = await api.get(`/categories/${kind}`);
      data = r.data;
    }
    // de-dup defensively by name
    const seen = new Set();
    const deduped = [];
    for (const c of data) {
      if (seen.has(c.name)) continue;
      seen.add(c.name);
      deduped.push(c);
    }
    deduped.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    setCategories(deduped);
  };

  const loadEntries = async () => {
    const params = {};
    if (scope.type === "date") params.date = scope.value;
    if (scope.type === "month") params.month = scope.value;
    if (scope.type === "week") params.week_start = scope.value;
    if (scope.type === "year") params.year = scope.value;
    const { data } = await api.get(`/ops/${kind}`, { params });
    setEntries(data);
  };

  useEffect(() => {
    loadCategories();
    // eslint-disable-next-line
  }, [kind]);

  useEffect(() => {
    loadEntries();
    // eslint-disable-next-line
  }, [scope.value]);

  const save = async (p) => {
    if (scope.type === "date") p.date = p.date || scope.value;
    if (scope.type === "month") p.month = p.month || scope.value;
    if (scope.type === "week") p.week_start = p.week_start || scope.value;
    if (p.id) await api.put(`/ops/${kind}/${p.id}`, p);
    else await api.post(`/ops/${kind}`, p);
    toast.success("Entry saved");
    loadEntries();
  };

  const del = async () => {
    if (modal.entry?.id) {
      await api.delete(`/ops/${kind}/${modal.entry.id}`);
      toast.success("Deleted");
      loadEntries();
    }
  };

  const visibleCategories = categories.filter((c) => !c.hidden);

  const grouped = useMemo(() => {
    const map = {};
    for (const c of visibleCategories) map[c.name] = [];
    for (const e of entries) {
      const k = e.category || visibleCategories[0]?.name;
      if (!map[k]) map[k] = [];
      map[k].push(e);
    }
    return map;
  }, [entries, visibleCategories]);

  const filterEntries = (arr) => {
    if (!q) return arr;
    const ql = q.toLowerCase();
    return arr.filter((e) => JSON.stringify(e).toLowerCase().includes(ql));
  };

  const stats = useMemo(() => {
    const total = entries.length;
    const completed = entries.filter((e) => ["completed", "closed"].includes((e.status || "").toLowerCase())).length;
    const cancelled = entries.filter((e) => ["cancelled", "rescheduled", "no-show"].includes((e.status || "").toLowerCase())).length;
    const priority = entries.filter((e) => (e.priority || "").toLowerCase() === "priority").length;
    const open = total - completed - cancelled;
    const followups = entries.filter((e) => e.follow_up || e.follow_up_date).length;
    return { total, completed, cancelled, priority, open, followups };
  }, [entries]);

  const categoryNames = visibleCategories.map((c) => c.name);
  const currentCat = modal.entry?.category || modal.defaults?.category || categoryNames[0];
  const dynamicFields = getFieldsFor ? getFieldsFor(currentCat) : (defaultCategories?.[0]?.fields || []);

  return (
    <div>
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        dot={dot}
        description={description}
        right={
          <>
            {scope.Navigator}
            {extraHeaderActions}
            <Button variant="outline" onClick={() => setCatMgrOpen(true)} data-testid="ops-categories-btn">
              <Settings size={14} className="mr-2" /> Categories
            </Button>
            <Button
              className="bg-[var(--rw-sidebar)] hover:bg-[var(--rw-sidebar-hover)]"
              onClick={() => setModal({ open: true, entry: null, defaults: { category: categoryNames[0] } })}
              data-testid="ops-add-btn"
            >
              <Plus size={14} className="mr-2" /> Add Entry
            </Button>
          </>
        }
      />
      <div className="p-8 space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {summaryFields.includes("total") && <StatCard label="Total" value={stats.total} testid="ops-stat-total" />}
          {summaryFields.includes("completed") && <StatCard label="Completed" value={stats.completed} />}
          {summaryFields.includes("open") && <StatCard label="Open" value={stats.open} />}
          {summaryFields.includes("priority") && <StatCard label="Priority" value={stats.priority} />}
          {summaryFields.includes("cancelled") && <StatCard label="Cancelled / No-show" value={stats.cancelled} />}
          {summaryFields.includes("followups") && <StatCard label="Follow-ups" value={stats.followups} />}
        </div>

        <div className="flex items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              placeholder="Search entries…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="ops-search"
            />
          </div>
        </div>

        <div>
          {prependSections && prependSections({ scope, entries, refresh: loadEntries })}
          {visibleCategories.map((c) => {
            let rows = filterEntries(grouped[c.name] || []);
            const injected = injectRowFor && injectRowFor(c.name);
            if (injected) rows = [...rows, injected];
            if (sortRowsBy) rows = [...rows].sort(sortRowsBy);
            return (
              <Section
                key={c.id}
                title={decorateTitle ? decorateTitle(c.name) : c.name}
                color={c.color}
                count={rows.length}
                action={
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setModal({ open: true, entry: null, defaults: { category: c.name } })}
                    data-testid={`add-in-${c.name}`}
                  >
                    <Plus size={12} className="mr-1" /> Add
                  </Button>
                }
              >
                {renderRows ? (
                  renderRows(rows, {
                    onRowClick: (r) => setModal({ open: true, entry: r, defaults: { category: c.name } }),
                    refresh: loadEntries,
                  })
                ) : (
                  <EntryTable
                    columns={columns}
                    rows={rows}
                    onRowClick={(r) => setModal({ open: true, entry: r, defaults: { category: c.name } })}
                    emptyText="No entries — click Add to log one."
                    rowActions={extraRowActions ? (r) => extraRowActions(r, loadEntries) : undefined}
                    rowClass={rowClass}
                  />
                )}
              </Section>
            );
          })}
        </div>
      </div>

      <EntryModal
        open={modal.open}
        onOpenChange={(v) => setModal({ ...modal, open: v })}
        title={modal.entry ? "Edit Entry" : "New Entry"}
        entry={modal.entry}
        defaults={modal.defaults}
        fields={[
          { name: "category", label: "Category", type: "select", options: categoryNames },
          ...dynamicFields,
        ]}
        onSave={save}
        onDelete={del}
      />

      <CategoryManager
        open={catMgrOpen}
        onOpenChange={setCatMgrOpen}
        kind={kind}
        onChanged={loadCategories}
      />
    </div>
  );
}
